// What the client DRAWS at a distance — the ring plan (renderconfig rings
// joined with the bake's levels), the level lattice stride, the one
// grid-quad triangulation, the scope focus ranges and the ribbon view
// ranges. PURE module shared by the WebXR client (esbuild import) and node
// (require: world/walls.js, the gates). Keep it dependency-free: no three,
// no JSON imports.
//
// Tile convention: a level tile is 257 x 257 uint16 samples (256 cells + the
// shared edge sample); sample k of a level sits at world (k + 0.5) * cell_m.
'use strict';

const TILE_N = 257;

// ------------------------------------------------------------- ring plan
// renderconfig.json rings -> the rings with the bake's level info joined in
// and the REAL extent in tiles (per-level grids are edge-padded to tile
// multiples; a coarse pad is up to a tile of fake repeated terrain, never
// requested).
// A ring flagged `frame: true` (the outermost, renderconfig) reaches the
// frame's diagonal: every tile of the frame is wanted from anywhere in it.
// At 8000 m the L4 disc left frame tiles undrawn from 77 % of St. Moritz
// positions (up to 12 of 28) — holes in the ground with the neighbours'
// skirts hanging in them, sky colour through the gap (validate.py
// _ring_radius is the twin).
function ringRadius(r, extentX, extentZ) {
  return r.frame ? Math.max(r.radius_m, Math.hypot(extentX, extentZ)) : r.radius_m;
}

function buildRings(cfgRings, levels, extentX, extentZ) {
  return cfgRings.map((r) => ({
    ...r,
    radius_m: ringRadius(r, extentX, extentZ),
    info: levels['L' + r.level],
    nxReal: Math.ceil(extentX / r.tile_m),
    nyReal: Math.ceil(extentZ / r.tile_m),
  })).filter((r) => r.info);
}

// ------------------------------------------------------------ level tiles
// drawn vertex spacing in level samples (256 % (patch_verts - 1) == 0, so
// integral: 1/2/4/8/16/32)
function levelStride(patchVerts) {
  return 256 / (patchVerts - 1);
}

// tile sample index of drawn vertex column/row ii at `stride`
function strideSrc(ii, stride) {
  return Math.round(ii * stride);
}

// ------------------------------------------------------- triangulation
// THE triangulation of a terrain grid quad (a = its top-left vertex, g = the
// row length): (a, a+g, a+1) and (a+1, a+g, a+g+1) — the diagonal runs from
// the (i+1, j) corner to the (i, j+1) corner. Writes 6 indices at q and
// returns the next q.
function quadIndex(idx, q, a, g) {
  idx[q++] = a; idx[q++] = a + g; idx[q++] = a + 1;
  idx[q++] = a + 1; idx[q++] = a + g; idx[q++] = a + g + 1;
  return q;
}

// Height inside a quad drawn by quadIndex, triangle-exact: corners h00 (i,j),
// h10 (i+1,j), h01 (i,j+1), h11 (i+1,j+1), fractions dx, dz in [0,1]. Bilinear
// can sit up to ~0.5 m under the drawn triangles in rocky cells, which reads
// as walking inside the ground.
function triSplit(h00, h10, h01, h11, dx, dz) {
  return (dx + dz < 1)
    ? h00 + dx * (h10 - h00) + dz * (h01 - h00)
    : h11 + (1 - dx) * (h01 - h11) + (1 - dz) * (h10 - h11);
}

// --------------------------------------------------------- scope focus ranges
// How far each scope-focus level (client/src/focus.js) streams, as a function
// of the optic's VERTICAL fov: a level reaches as far as one of its drawn vertex
// spacings still subtends `px_per_vertex` render-target pixels,
//   range(fov) = clamp(spacing / (px_per_vertex * fov_rad / rt_px),
//                      range_min_m, range_max_m),  spacing = tile_m / (patch_verts - 1)
// so zooming in pushes every level out. Levels are then made strictly
// increasing (range(L_k) >= range(L_k-1) + tile_m(L_k)): a coarser focus level
// always reaches further than a finer one (the levels nest).
// specs: [{level, tile_m, patch_verts, px_per_vertex, range_min_m, range_max_m}]
// sorted by level. Returns {level: range_m}.
// Python twin: validate.py _focus_ranges — change both or neither.
function focusLevelRange(spec, fovDeg, rtPx) {
  const spacing = spec.tile_m / (spec.patch_verts - 1);
  const pxRad = (fovDeg * Math.PI / 180) / rtPx;
  const r = spacing / (spec.px_per_vertex * pxRad);
  return Math.min(spec.range_max_m, Math.max(spec.range_min_m, r));
}
function focusRanges(specs, fovDeg, rtPx) {
  const out = {};
  let prev = -Infinity;
  for (const s of specs) {
    const r = Math.max(focusLevelRange(s, fovDeg, rtPx), prev + s.tile_m);
    out[s.level] = r;
    prev = r;
  }
  return out;
}
// The fovs a want-set sweep must visit: the two ends of the zoom range and
// every level's knee (the fov where its unclamped range reaches range_max_m)
// inside it. Between knees a level's range grows like 1/fov while the wedge
// narrows like fov, so the tile count only ever peaks at a knee or an end.
function focusSweepFovs(specs, rtPx, fovLo, fovHi) {
  const out = new Set([fovHi, fovLo]);
  for (const s of specs) {
    const spacing = s.tile_m / (s.patch_verts - 1);
    const fov = (spacing * rtPx / (s.px_per_vertex * s.range_max_m)) * 180 / Math.PI;
    if (fov > fovLo && fov < fovHi) out.add(fov);
  }
  return [...out].sort((a, b) => b - a);
}

// ------------------------------------------------------------- ribbons
// Road/rail ribbons are chunked by RIBBON_CHUNK m and shown per class while
// the chunk centre is within the class view range (+0.7 chunk); beyond it the
// baked ortho texture carries the road.
const RIBBON_CHUNK = 512;
const RIBBON_VIEW = { major: 800, minor: 520, path: 280, rail: 1000 };
function ribbonVisible(cls, chunkDist) {
  return chunkDist < (RIBBON_VIEW[cls] || 500) + RIBBON_CHUNK * 0.7;
}

module.exports = {
  TILE_N, buildRings, levelStride, strideSrc,
  quadIndex, triSplit, ringRadius,
  focusLevelRange, focusRanges, focusSweepFovs,
  RIBBON_CHUNK, RIBBON_VIEW, ribbonVisible,
};
